import React, { memo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import type { NodeProps } from 'reactflow';
import {
  Text,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Key20Regular, Link20Regular } from '@fluentui/react-icons';
import type { PhysicalColumn } from './types';

export interface SchemaTableNodeData {
  label: string;
  columns: PhysicalColumn[];
  connectionName: string;
  rowCount?: number | null;
  isHighlighted: boolean;
  isFaded: boolean;
  isSelected: boolean;
}

const MAX_VISIBLE_COLUMNS = 7;

const useStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1.5px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
    minWidth: '230px',
    maxWidth: '290px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'opacity 0.2s, border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
    overflow: 'visible',
  },
  containerSelected: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    borderTopWidth: '2px',
    borderRightWidth: '2px',
    borderBottomWidth: '2px',
    borderLeftWidth: '2px',
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: `0 4px 16px ${tokens.colorBrandShadowAmbient}`,
  },
  containerHighlighted: {
    borderTopColor: tokens.colorPaletteBerryBorder2,
    borderRightColor: tokens.colorPaletteBerryBorder2,
    borderBottomColor: tokens.colorPaletteBerryBorder2,
    borderLeftColor: tokens.colorPaletteBerryBorder2,
    backgroundColor: tokens.colorPaletteBerryBackground2,
    boxShadow: '0 3px 12px rgba(139,92,246,0.12)',
    animationName: {
      from: { boxShadow: '0 0 0 0 rgba(139,92,246,0.4)' },
      '50%': { boxShadow: '0 0 0 6px rgba(139,92,246,0.15)' },
      to: { boxShadow: '0 3px 12px rgba(139,92,246,0.12)' },
    },
    animationDuration: '0.6s',
    animationTimingFunction: 'ease-out',
    animationIterationCount: '1',
  },
  containerFaded: {
    opacity: 0.25,
  },
  header: {
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  headerSelected: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  headerHighlighted: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
  },
  headerMeta: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  columns: {
    padding: '4px 0',
  },
  columnRow: {
    padding: '2px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    lineHeight: '18px',
  },
  columnRowRelation: {
    // no background change — only text color via columnNameRelation
  },
  columnName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  columnNameRelation: {
    color: tokens.colorBrandForeground1,
    fontWeight: 500,
  },
  columnType: {
    color: tokens.colorNeutralForeground3,
    fontSize: '10px',
    flexShrink: 0,
  },
  footer: {
    padding: '4px 12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'center',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  rowCountBadge: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
  },
});

function formatRowCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
}

const SchemaTableNode: React.FC<NodeProps<SchemaTableNodeData>> = ({ data }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);

  const shouldCollapse = data.columns.length > MAX_VISIBLE_COLUMNS;
  const visibleColumns = expanded || !shouldCollapse
    ? data.columns
    : data.columns.slice(0, MAX_VISIBLE_COLUMNS);
  const hiddenCount = data.columns.length - MAX_VISIBLE_COLUMNS;

  let containerClass = styles.container;
  if (data.isSelected) containerClass += ' ' + styles.containerSelected;
  else if (data.isHighlighted) containerClass += ' ' + styles.containerHighlighted;
  if (data.isFaded) containerClass += ' ' + styles.containerFaded;

  let headerClass = styles.header;
  if (data.isSelected) headerClass += ' ' + styles.headerSelected;
  else if (data.isHighlighted) headerClass += ' ' + styles.headerHighlighted;

  return (
    <div className={containerClass} style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden' }} />

      <div className={headerClass}>
        <Text weight="semibold" size={300} truncate wrap={false}>
          {data.label}
        </Text>
        <div className={styles.headerMeta}>
          {data.rowCount != null && (
            <span className={styles.rowCountBadge}>{formatRowCount(data.rowCount)} rows</span>
          )}
          <Badge appearance="outline" size="small" color="informative">
            {data.columns.length}
          </Badge>
        </div>
      </div>

      <div className={styles.columns}>
        {visibleColumns.map((col) => {
          const isRel = col.isRelationshipColumn;
          const rowClass = styles.columnRow + (isRel ? ' ' + styles.columnRowRelation : '');
          const nameClass = styles.columnName + (isRel ? ' ' + styles.columnNameRelation : '');
          return (
            <div key={col.name} className={rowClass}>
              {col.isPrimaryKey && <Key20Regular fontSize={11} style={{ color: tokens.colorPaletteMarigoldForeground1, flexShrink: 0 }} />}
              {col.isForeignKey && !col.isPrimaryKey && <Link20Regular fontSize={11} style={{ color: tokens.colorBrandForeground1, flexShrink: 0 }} />}
              {!col.isPrimaryKey && !col.isForeignKey && <span style={{ width: 11, flexShrink: 0 }} />}
              <span className={nameClass}>{col.name}</span>
              <span className={styles.columnType}>{col.type}</span>
            </div>
          );
        })}
      </div>

      {shouldCollapse && (
        <div
          className={styles.footer}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        >
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {expanded ? '▲ Show less' : `▼ + ${hiddenCount} more field${hiddenCount !== 1 ? 's' : ''}`}
          </Text>
        </div>
      )}
    </div>
  );
};

export default memo(SchemaTableNode);